import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import ReactSpinner from '../../components/reactSpinner/ReactSpinner';
import { messageToast } from '../../utils/define';
import { nhiemVuKhacGetItemAction, editNhiemVuKhacAction } from '../../store/reducers/nhiemvukhac';
import { nhanVienGetAllAction } from '../../store/reducers/nhanVien';
import { getNguoiDungById } from '../../store/reducers/nguoidung';
import { TRANG_THAI_KHAC_OPTIONS, MUC_DO_UU_TIEN } from '../../store/constants';

const schema = yup.object().shape({
  title: yup.string().required('Vui lòng nhập Tiêu đề'),

  mucDo: yup
    .number()
    .transform((v, o) => (o === '' || o === null || o === undefined ? null : v))
    .typeError('Vui lòng chọn mức độ')
    .required('Vui lòng chọn mức độ'),

  // không bắt buộc
  hanChot: yup.string().nullable().notRequired(),

  ngayGiao: yup.string().nullable(),
  ngayKetThucThucTe: yup.string().nullable(),

  nhanVienId: yup
    .number()
    .transform((v, o) => (o === '' || o === null || o === undefined ? null : v))
    .typeError('Vui lòng chọn nhân viên')
    .required('Vui lòng chọn nhân viên'),

  // không bắt buộc
  status: yup
    .number()
    .transform((v, o) => (o === '' || o === null || o === undefined ? null : v))
    .nullable()
    .notRequired(),
});

const normalizeDateForInput = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  if (typeof dateStr === 'string' && dateStr.indexOf('T') !== -1) return dateStr.split('T')[0];
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeEnumValue = (raw, options, emptyValue = '') => {
  if (raw === null || raw === undefined || raw === '') return emptyValue;

  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;

  const s = String(raw).trim();

  const n = Number(s);
  if (!Number.isNaN(n) && options.some(o => Number(o.value) === n)) return n;

  const byKey = options.find(o => String(o.key).toLowerCase() === s.toLowerCase());
  if (byKey) return Number(byKey.value);

  const byLabel = options.find(o => String(o.label).toLowerCase() === s.toLowerCase());
  if (byLabel) return Number(byLabel.value);

  return emptyValue;
};

const NhiemVuKhacEdit = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const nhiemVuKhacItem = useSelector(state => state.nhiemVuKhac.nhiemVuKhacItem);
  const listNhanVien = useSelector(state => state.nhanVien.list || []);

  const [isLoading, setIsLoading] = useState(false);

  const [nhanVienKeyword, setNhanVienKeyword] = useState('');

  const getNhanVienLabel = (nv) =>
    nv?.tenNhanVien || nv?.hoTen || nv?.name || nv?.ten || nv?.displayName || '';

  const normalizeText = (s) => {
    if (!s) return '';
    try {
      return s
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    } catch {
      return s.toString().toLowerCase().trim();
    }
  };

  const filteredNhanVien = useMemo(() => {
    const kw = normalizeText(nhanVienKeyword);
    if (!kw) return listNhanVien;

    return (listNhanVien || []).filter((nv) => {
      const name = normalizeText(getNhanVienLabel(nv));
      return name.includes(kw);
    });
  }, [listNhanVien, nhanVienKeyword]);

  const [nvOpen, setNvOpen] = useState(false);
  const [nvActiveIndex, setNvActiveIndex] = useState(-1);
  const nvBoxRef = useRef(null);
  const nvMenuRef = useRef(null);

  useEffect(() => {
    if (!nvOpen) return;
    const menu = nvMenuRef.current;
    if (!menu) return;

    const activeEl = menu.querySelector('.dropdown-item.active');
    if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
  }, [nvOpen, nvActiveIndex]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (!nvBoxRef.current) return;
      if (!nvBoxRef.current.contains(e.target)) {
        setNvOpen(false);
        setNvActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const didInitNhanVienKeywordRef = useRef(false);
  useEffect(() => {
    didInitNhanVienKeywordRef.current = false;
  }, [nhiemVuKhacItem?.id]);

  useEffect(() => {
    if (didInitNhanVienKeywordRef.current) return;
    const id = nhiemVuKhacItem?.nhanVienId;
    if (!id) return;
    if (!Array.isArray(listNhanVien) || listNhanVien.length === 0) return;

    const nv = listNhanVien.find((x) => Number(x.id) === Number(id));
    if (nv) setNhanVienKeyword(getNhanVienLabel(nv));

    didInitNhanVienKeywordRef.current = true;
  }, [nhiemVuKhacItem?.id, nhiemVuKhacItem?.nhanVienId, listNhanVien.length]);

  const selectNhanVien = (nv, setFieldValue) => {
    if (!nv) return;
    setFieldValue('nhanVienId', Number(nv.id));
    setNhanVienKeyword(getNhanVienLabel(nv)); 
    setNvOpen(false);
    setNvActiveIndex(-1);
  };

  const handleNhanVienInputChange = (e, setFieldValue) => {
    const v = e.target.value;
    setNhanVienKeyword(v);

    setFieldValue('nhanVienId', '');

    setNvOpen(true);
    setNvActiveIndex(0);
  };

  const handleNhanVienKeyDown = (e, setFieldValue) => {
    if (e.key === 'Tab') {
      setNvOpen(false);
      setNvActiveIndex(-1);
      return;
    }

    if (!nvOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setNvOpen(true);
      setNvActiveIndex(0);
      return;
    }
    if (!nvOpen) return;

    if (e.key === 'Escape') {
      setNvOpen(false);
      setNvActiveIndex(-1);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setNvActiveIndex((prev) => {
        const next = prev + 1;
        return next >= filteredNhanVien.length ? filteredNhanVien.length - 1 : next;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setNvActiveIndex((prev) => (prev - 1 < 0 ? 0 : prev - 1));
      return;
    }

    if (e.key === 'Enter') {
      if (nvActiveIndex >= 0 && nvActiveIndex < filteredNhanVien.length) {
        e.preventDefault();
        selectNhanVien(filteredNhanVien[nvActiveIndex], setFieldValue);
      }
    }
  };

  // display name of creator
  const [nguoiTaoName, setNguoiTaoName] = useState('');


  // file states theo code mẫu project
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [filesMarkedForDelete, setFilesMarkedForDelete] = useState([]);
  const newFileInputRef = useRef(null);

  const toFileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = bytes;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
      v = v / 1024;
      i += 1;
    }
    return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
  };

  const smallBtnStyle = { padding: '2px 10px', fontSize: 12, lineHeight: '18px' };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    if (id) dispatch(nhiemVuKhacGetItemAction(id));
  }, [dispatch, location.search]);

  useEffect(() => {
    if (!listNhanVien.length) dispatch(nhanVienGetAllAction());
  }, [dispatch, listNhanVien.length]);

  useEffect(() => {
    const files = nhiemVuKhacItem?.files || nhiemVuKhacItem?.Files || [];
    setExistingFiles(Array.isArray(files) ? files : []);
    setNewFiles([]);
    setFilesMarkedForDelete([]);
    if (newFileInputRef.current) newFileInputRef.current.value = '';
  }, [nhiemVuKhacItem]);

  // fetch display name for nguoiDungId
  useEffect(() => {
    let mounted = true;
    (async () => {
      const id = nhiemVuKhacItem?.nguoiDungId;
      if (!id) return;

      try {
        const res = await dispatch(getNguoiDungById(id)).unwrap();
        let item = res || null;
        if (Array.isArray(item)) item = item[0] || null;
        if (item?.data) item = item.data;
        if (Array.isArray(item)) item = item[0] || null;

        const name = item?.hoTen || item?.fullName || item?.name || item?.ten || item?.displayName || '';
        if (mounted) setNguoiTaoName(name);
      } catch (_) {
        if (mounted) setNguoiTaoName('');
      }
    })();
    return () => { mounted = false; };
  }, [dispatch, nhiemVuKhacItem?.nguoiDungId]);

  const initialValues = useMemo(() => ({
    title: nhiemVuKhacItem?.title || '',
    moTa: nhiemVuKhacItem?.moTa || '',
    mucDo: normalizeEnumValue(nhiemVuKhacItem?.mucDo, MUC_DO_UU_TIEN, ''),
    ngayGiao: normalizeDateForInput(nhiemVuKhacItem?.ngayGiao),
    hanChot: normalizeDateForInput(nhiemVuKhacItem?.hanChot),
    ngayKetThucThucTe: normalizeDateForInput(nhiemVuKhacItem?.ngayKetThucThucTe),
    nhanVienId: nhiemVuKhacItem?.nhanVienId || '',
    status: normalizeEnumValue(nhiemVuKhacItem?.status, TRANG_THAI_KHAC_OPTIONS, ''),
  }), [nhiemVuKhacItem]);

  const toggleMarkDeleteExistingFile = (fileId) => {
    setFilesMarkedForDelete(prev => {
      const s = new Set(prev);
      if (s.has(fileId)) s.delete(fileId);
      else s.add(fileId);
      return Array.from(s);
    });
  };

  const handlePickNewFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    setNewFiles((prev) => {
      const map = new Map(prev.map((f) => [toFileKey(f), f]));
      picked.forEach((f) => map.set(toFileKey(f), f));
      return Array.from(map.values());
    });

    if (newFileInputRef.current) newFileInputRef.current.value = '';
  };

  const removeNewFile = (idx) => {
    setNewFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const clearNewFiles = () => {
    setNewFiles([]);
    if (newFileInputRef.current) newFileInputRef.current.value = '';
  };

  const handleEdit = async (values) => {
    if (!nhiemVuKhacItem?.id) {
      toast.error('Không tìm thấy Nhiệm vụ để cập nhật!');
      return;
    }

    const nguoiDungIdToSend = nhiemVuKhacItem?.nguoiDungId;
    if (!nguoiDungIdToSend) {
      toast.error('Thiếu NguoiDungId trên bản ghi. Vui lòng kiểm tra dữ liệu!');
      return;
    }

    const statusToSend =
      values.status === '' || values.status === null || values.status === undefined
        ? Number(normalizeEnumValue(nhiemVuKhacItem?.status, TRANG_THAI_KHAC_OPTIONS, 0))
        : Number(values.status);

    const payload = {
      id: nhiemVuKhacItem.id,
      data: {
        Title: values.title,
        MoTa: values.moTa,
        MucDo: Number(values.mucDo),
        NgayGiao: values.ngayGiao || null,
        HanChot: values.hanChot || null,
        NgayKetThucThucTe: values.ngayKetThucThucTe || null,
        NhanVienId: Number(values.nhanVienId),
        NguoiDungId: nguoiDungIdToSend,
        Status: statusToSend,
      },
      files: newFiles,
      removeFileIds: filesMarkedForDelete
    };

    try {
      setIsLoading(true);
      const status = await dispatch(editNhiemVuKhacAction(payload)).unwrap();
      if (status >= 200 && status < 300) {
        toast.success(messageToast.messageToastUpdateSuccess);
        history.goBack();
      } else {
        toast.error(messageToast.messageToastUpdateError);
      }
    } catch (error) {
      toast.error(error?.message || 'Đã có lỗi xảy ra, vui lòng thử lại!');
    } finally {
      setIsLoading(false);
    }
  };

  if (!nhiemVuKhacItem) return <ReactSpinner loadings={true} />;

  return (
    <Card>
      <Card.Header as="h5">Sửa Nhiệm vụ khác</Card.Header>
      <Card.Body>
        <Formik
          enableReinitialize
          validationSchema={schema}
          onSubmit={handleEdit}
          initialValues={initialValues}
        >
          {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
            <Form noValidate onSubmit={handleSubmit}>
              {/* Người tạo */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Người tạo</Form.Label>
                <Col sm="10">
                  <Form.Control type="text" value={nguoiTaoName || ''} disabled readOnly />
                </Col>
              </Form.Group>

              {/* Tiêu đề */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Tiêu đề *</Form.Label>
                <Col sm="10">
                  <Form.Control
                    type="text"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                </Col>
              </Form.Group>

              {/* Mô tả */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Mô tả</Form.Label>
                <Col sm="10">
                  <Form.Control as="textarea" rows={3} name="moTa" value={values.moTa} onChange={handleChange} />
                </Col>
              </Form.Group>

              {/* Mức độ */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Mức độ *</Form.Label>
                <Col sm="10">
                  <Form.Control
                    as="select"
                    name="mucDo"
                    value={values.mucDo}
                    onChange={(e) => setFieldValue('mucDo', e.target.value === '' ? '' : Number(e.target.value))}
                    isInvalid={!!errors.mucDo}
                  >
                    <option value="">-- Chọn mức độ --</option>
                    {MUC_DO_UU_TIEN.map(opt => (
                      <option key={opt.key} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">{errors.mucDo}</Form.Control.Feedback>
                </Col>
              </Form.Group>

              {/* Ngày giao */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Ngày giao</Form.Label>
                <Col sm="10">
                  <Form.Control type="date" name="ngayGiao" value={values.ngayGiao} onChange={handleChange} />
                </Col>
              </Form.Group>

              {/* Hạn chót (không bắt buộc) */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Hạn chót</Form.Label>
                <Col sm="10">
                  <Form.Control
                    type="date"
                    name="hanChot"
                    value={values.hanChot}
                    onChange={handleChange}
                    isInvalid={!!errors.hanChot}
                  />
                  <Form.Control.Feedback type="invalid">{errors.hanChot}</Form.Control.Feedback>
                </Col>
              </Form.Group>

              {/* Ngày hoàn thành thực tế */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Ngày hoàn thành thực tế</Form.Label>
                <Col sm="10">
                  <Form.Control type="date" name="ngayKetThucThucTe" value={values.ngayKetThucThucTe} onChange={handleChange} />
                </Col>
              </Form.Group>

              {/* Nhân viên */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Nhân viên *</Form.Label>

                <Col sm="10">
                  <div ref={nvBoxRef} style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      placeholder="Chọn nhân viên..."
                      value={nhanVienKeyword}
                      onFocus={() => {
                        setNvOpen(true);
                        setNvActiveIndex(filteredNhanVien.length > 0 ? 0 : -1);
                      }}
                      onClick={() => {
                        setNvOpen(true);
                        setNvActiveIndex(filteredNhanVien.length > 0 ? 0 : -1);
                      }}
                      onChange={(e) => handleNhanVienInputChange(e, setFieldValue)}
                      onKeyDown={(e) => handleNhanVienKeyDown(e, setFieldValue)}
                      disabled={isLoading}
                      isInvalid={!!errors.nhanVienId}
                    />

                    {nvOpen && (
                      <div
                        ref={nvMenuRef}
                        className="dropdown-menu show w-100"
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: 'calc(100% + 4px)',
                          zIndex: 20,
                          maxHeight: 260,
                          overflowY: 'auto',
                        }}
                      >
                        {filteredNhanVien.length === 0 ? (
                          <div className="dropdown-item text-muted small">
                            Không tìm thấy nhân viên phù hợp.
                          </div>
                        ) : (
                          filteredNhanVien.map((nv, idx) => {
                            const active = idx === nvActiveIndex;
                            return (
                              <button
                                type="button"
                                key={nv.id}
                                className={`dropdown-item ${active ? 'active' : ''}`}
                                onMouseEnter={() => setNvActiveIndex(idx)}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectNhanVien(nv, setFieldValue);
                                }}
                              >
                                {getNhanVienLabel(nv)}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    <Form.Control.Feedback type="invalid">
                      {errors.nhanVienId}
                    </Form.Control.Feedback>
                  </div>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Trạng thái</Form.Label>
                <Col sm="10">
                  <Form.Control
                    as="select"
                    name="status"
                    value={values.status === null || values.status === undefined ? '' : values.status}
                    onChange={(e) => setFieldValue('status', e.target.value === '' ? '' : Number(e.target.value))}
                    isInvalid={!!errors.status}
                  >
                    {TRANG_THAI_KHAC_OPTIONS.map(opt => (
                      <option key={opt.key} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">{errors.status}</Form.Control.Feedback>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Tệp hiện tại</Form.Label>
                <Col sm="10">
                  {existingFiles.length > 0 ? (
                    <Row className="mt-1">
                      <Col md={6}>
                        <div className="border rounded">
                          <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                            <div className="text-muted">
                              Có {existingFiles.length} tệp
                              {filesMarkedForDelete.length > 0 ? ` (đã chọn xóa ${filesMarkedForDelete.length})` : ''}
                            </div>

                            {filesMarkedForDelete.length > 0 && (
                              <Button
                                type="button"
                                theme="soft"
                                size="sm"
                                onClick={() => setFilesMarkedForDelete([])}
                                disabled={isLoading}
                              >
                                Bỏ chọn xóa
                              </Button>
                            )}
                          </div>

                          <div className="px-2 pb-1">
                            {existingFiles.map((f, idx) => {
                              const id = f.id || f.Id;
                              const name = f.name || f.Name || 'Tệp đính kèm';
                              const marked = filesMarkedForDelete.includes(id);

                              return (
                                <div
                                  key={id}
                                  className={`d-flex justify-content-between align-items-center py-2 ${
                                    idx < existingFiles.length - 1 ? 'border-bottom' : ''
                                  }`}
                                >
                                  <div className="me-3" style={{ minWidth: 0 }}>
                                    <div
                                      className={`fw-semibold text-truncate ${marked ? 'text-muted' : ''}`}
                                      style={{ maxWidth: '100%' }}
                                      title={name}
                                    >
                                      {name}
                                    </div>

                                    {marked && (
                                      <div className="text-muted small">
                                        Sẽ xóa khi bấm Lưu
                                      </div>
                                    )}
                                  </div>

                                  <Button
                                    type="button"
                                    theme={marked ? 'secondary' : 'danger'}
                                    size="sm"
                                    style={smallBtnStyle}
                                    onClick={() => toggleMarkDeleteExistingFile(id)}
                                    disabled={isLoading}
                                  >
                                    {marked ? 'Hoàn tác' : 'Xóa'}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <div className="text-muted">Không có tệp.</div>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Thêm tệp mới</Form.Label>
                <Col sm="10">
                  <Form.Control
                    ref={newFileInputRef}
                    type="file"
                    multiple
                    onChange={handlePickNewFiles}
                    disabled={isLoading}
                  />

                  {newFiles.length > 0 && (
                    <Row className="mt-2">
                      <Col md={6}>
                        <div className="border rounded">
                          <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                            <div className="text-muted">Đã chọn {newFiles.length} file</div>
                            <Button
                              type="button"
                              theme="soft"
                              size="sm"
                              onClick={clearNewFiles}
                              disabled={isLoading}
                            >
                              Bỏ tất cả
                            </Button>
                          </div>

                          <div className="px-2 pb-1">
                            {newFiles.map((f, idx) => (
                              <div
                                key={toFileKey(f)}
                                className={`d-flex justify-content-between align-items-center py-2 ${
                                  idx < newFiles.length - 1 ? 'border-bottom' : ''
                                }`}
                              >
                                <div className="me-3" style={{ minWidth: 0 }}>
                                  <div className="fw-semibold text-truncate" title={f.name}>
                                    {f.name}
                                  </div>
                                  <div className="text-muted small">
                                    {formatBytes(f.size)}
                                  </div>
                                </div>

                                <Button
                                  type="button"
                                  theme="danger"
                                  size="sm"
                                  style={smallBtnStyle}
                                  onClick={() => removeNewFile(idx)}
                                  disabled={isLoading}
                                >
                                  Xóa
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Form.Group>

              {/* Buttons */}
              <Form.Group as={Row} className="mb-3">
                <Col md={{ span: 10, offset: 2 }}>
                  <ButtonGroup>
                    <Button type="submit" theme="primary" disabled={isLoading}>
                      {isLoading ? 'Đang lưu...' : 'Lưu'}
                    </Button>
                    &nbsp;&nbsp;&nbsp;
                    <Button theme="secondary" onClick={() => history.goBack()} disabled={isLoading}>
                      Hủy
                    </Button>
                  </ButtonGroup>
                </Col>
              </Form.Group>
            </Form>
          )}
        </Formik>

        <ReactSpinner loadings={isLoading} />
      </Card.Body>
    </Card>
  );
};

export default NhiemVuKhacEdit;
